import { useCallback, useState } from 'react';
import { useForm } from '@refinedev/antd';
import { HttpError } from '@refinedev/core';
import { Button, Form, Input, Switch, Tooltip, Space, Typography } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useParams } from 'react-router';
import type { Company } from '@viq-admin/types';
import { RequireCan } from '../../components/RequireCan';
import { COMPANY_CRM_FLAGS, COMPANY_FLAG_HELP } from './companyFlagHelp';

/**
 * Edit a company (tenant) as a BARE form — no Refine <Edit> page chrome — so it
 * can render in place inside the shared DetailPage. Save does NOT navigate
 * (`redirect: false`); success is reported via `onSaved` so DetailPage flips
 * back to read mode. The form derives its own `dirty` (useForm exposes none)
 * and reports it via `onDirtyChange`. `warnWhenUnsavedChanges: false` — the
 * DetailPage owns the discard guard.
 *
 * All props are optional so the standalone edit test can render <CompanyEdit/>.
 * `id` is passed explicitly for robustness (at /companies/:id the inferred Refine
 * action is `show`, not `edit`); it falls back to the route param.
 */
interface UpdateCompanyInput {
  name?: string;
  entraHint?: string | null;
  storeCardImages?: boolean;
  crmHubspotEnabled?: boolean;
  crmSalesforceEnabled?: boolean;
  crmZohoEnabled?: boolean;
  leadMoveEnabled?: boolean;
  autoEnrichApollo?: boolean;
}

export interface CompanyEditProps {
  id?: string;
  onSaved?: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}

export function CompanyEdit({ id, onSaved, onDirtyChange }: CompanyEditProps = {}) {
  const params = useParams<{ id: string }>();
  const recordId = id ?? params.id;
  const [, setDirty] = useState(false);

  const { formProps, saveButtonProps, query, onFinish } = useForm<
    Company,
    HttpError,
    UpdateCompanyInput
  >({
    resource: 'companies',
    action: 'edit',
    id: recordId,
    redirect: false,
    warnWhenUnsavedChanges: false,
    onMutationSuccess: () => {
      setDirtyOnce(false);
      onSaved?.();
    },
  });
  const record = query?.data?.data;

  const setDirtyOnce = useCallback(
    (next: boolean) => {
      setDirty((prev) => {
        if (prev !== next) onDirtyChange?.(next);
        return next;
      });
    },
    [onDirtyChange],
  );

  return (
    <RequireCan I="manage" a="Company">
      <Form
        {...formProps}
        layout="vertical"
        initialValues={record}
        onValuesChange={() => setDirtyOnce(true)}
        onFinish={(values: UpdateCompanyInput) => {
          onFinish({
            name: values.name,
            entraHint: values.entraHint ?? null,
            storeCardImages: values.storeCardImages,
            crmHubspotEnabled: values.crmHubspotEnabled,
            crmSalesforceEnabled: values.crmSalesforceEnabled,
            crmZohoEnabled: values.crmZohoEnabled,
            leadMoveEnabled: values.leadMoveEnabled,
            autoEnrichApollo: values.autoEnrichApollo,
          });
        }}
      >
        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true, message: 'Name is required' }]}
        >
          <Input maxLength={200} />
        </Form.Item>
        <Form.Item label="Entra Hint" name="entraHint">
          <Input maxLength={200} />
        </Form.Item>
        <Form.Item
          name="storeCardImages"
          valuePropName="checked"
          label={
            <Space>
              Store card images
              <Tooltip title={COMPANY_FLAG_HELP.storeCardImages}>
                <QuestionCircleOutlined />
              </Tooltip>
            </Space>
          }
        >
          <Switch aria-label="Store card images" />
        </Form.Item>
        <Form.Item
          name="leadMoveEnabled"
          valuePropName="checked"
          label={
            <Space>
              Move leads between conferences
              <Tooltip title={COMPANY_FLAG_HELP.leadMoveEnabled}>
                <QuestionCircleOutlined />
              </Tooltip>
            </Space>
          }
        >
          <Switch aria-label="Move leads between conferences" />
        </Form.Item>
        <Form.Item
          name="autoEnrichApollo"
          valuePropName="checked"
          label={
            <Space>
              Auto enrichment
              <Tooltip title={COMPANY_FLAG_HELP.autoEnrichApollo}>
                <QuestionCircleOutlined />
              </Tooltip>
            </Space>
          }
        >
          <Switch aria-label="Auto enrichment" />
        </Form.Item>
        <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
          CRM integrations
        </Typography.Text>
        {COMPANY_CRM_FLAGS.map(({ name, label }) => (
          <Form.Item
            key={name}
            name={name}
            valuePropName="checked"
            label={
              <Space>
                {label}
                <Tooltip title={COMPANY_FLAG_HELP[name]}>
                  <QuestionCircleOutlined />
                </Tooltip>
              </Space>
            }
          >
            <Switch aria-label={`${label} integration`} />
          </Form.Item>
        ))}
        <Form.Item>
          {/*
            No htmlType="submit" here: saveButtonProps.onClick already calls
            form.submit() once. antd's Button defaults to htmlType="button",
            so a click drives exactly that one submit path — a submit button
            inside this <Form> would ALSO trigger the browser's native form
            submit, invoking rc-field-form's own onSubmit -> form.submit()
            a second time.
          */}
          <Button type="primary" {...saveButtonProps}>
            Save
          </Button>
        </Form.Item>
      </Form>
    </RequireCan>
  );
}
